import React, { useEffect, useState } from 'react';
import {
  Box, FormControl, InputLabel, Select, MenuItem, TextField,
  List, ListItem, ListItemText, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Typography, Chip, Link,
  Pagination, Stack
} from '@mui/material';
import { Delete, FiberManualRecord } from '@mui/icons-material';
import api from '../utils/api';
import { sanitizedHtml } from '../utils/sanitize';
import { useAuth } from '../AuthContext';

function PendingRecordingsPage() {
  const [allPrograms, setAllPrograms] = useState([]);
  const [stationFilter, setStationFilter] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [stationList, setStationList] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const { isAdmin } = useAuth();

  useEffect(() => {
    document.title = '預約管理';
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await api.get('/programs', { params: { status: 'pending,recording' } });
      const programs = response.data || [];
      setAllPrograms(programs);
      const uniqueStations = Array.from(new Set(programs.map(p => `${p.station_id}__${p.station_name || ''}`)));
      setStationList(uniqueStations);
    } catch (err) {
      console.error('無法取得資料:', err);
    }
  };

  const getSortedFilteredPrograms = () => {
    let filtered = [...allPrograms];

    if (stationFilter) {
      const stationId = stationFilter.split('__')[0];
      filtered = filtered.filter(p => p.station_id === stationId);
    }

    if (searchKeyword) {
      filtered = filtered.filter(p => {
        if (p.title && p.title.includes(searchKeyword)) return true;
        if (Array.isArray(p.person) && p.person.some(x => x?.name?.includes(searchKeyword))) return true;
        return false;
      });
    }

    filtered.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
    return filtered;
  };

  const paginatedPrograms = () => {
    const sortedFiltered = getSortedFilteredPrograms();
    const start = (page - 1) * rowsPerPage;
    return sortedFiltered.slice(start, start + rowsPerPage);
  };

  const totalPages = Math.ceil(getSortedFilteredPrograms().length / rowsPerPage);

  const formatProgramTime = (timeString) => {
    if (!timeString || timeString.length < 12) return '時間未知';
    const year = timeString.slice(0, 4);
    const month = timeString.slice(4, 6);
    const day = timeString.slice(6, 8);
    const hour = timeString.slice(8, 10);
    const minute = timeString.slice(10, 12);
    return `${year}/${month}/${day} ${hour}:${minute}`;
  };

  const handleDeleteClick = (program) => {
    setProgramToDelete(program);
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!programToDelete) return;

    try {
      await api.delete(`/programs/${programToDelete.id}`);
      fetchPrograms();
      setConfirmDelete(false);
      setProgramToDelete(null);
    } catch (err) {
      console.error('刪除失敗:', err);
      alert('刪除失敗，請稍後再試');
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete(false);
    setProgramToDelete(null);
  };

  return (
    <Box>
      <FormControl fullWidth margin="normal">
        <InputLabel id="station-select-label">電台</InputLabel>
        <Select
          labelId="station-select-label"
          id="station-select"
          value={stationFilter}
          label="電台"
          onChange={(e) => {
            setStationFilter(e.target.value);
            setPage(1);
          }}
          renderValue={(selected) => {
            const match = stationList.find(s => s === selected);
            return match ? match.split('__')[1] : '全部';
          }}
        >
          <MenuItem value="">全部</MenuItem>
          {stationList.map(station => {
            const [id, name] = station.split('__');
            return <MenuItem key={station} value={station}>{name || id}</MenuItem>;
          })}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        margin="normal"
        label="搜尋節目或演出者"
        value={searchKeyword}
        onChange={(e) => {
          setSearchKeyword(e.target.value);
          setPage(1);
        }}
      />

      <List>
        {paginatedPrograms().map((program) => (
          <ListItem
            key={program.id}
            button
            onClick={() => {
              setSelectedProgram(program);
              setOpenDialog(true);
            }}
            secondaryAction={
              isAdmin && (
                <IconButton edge="end" onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClick(program);
                }}>
                  <Delete />
                </IconButton>
              )
            }
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {program.status === 'recording' && (
                    <FiberManualRecord color="error" fontSize="small" sx={{ mr: 1 }} />
                  )}
                  {program.title}
                </Box>
              }
              secondary={`時間：${formatProgramTime(program.start_time)} - ${formatProgramTime(program.end_time)}｜頻道：${program.station_name || program.station_id}`}
            />
          </ListItem>
        ))}
      </List>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
        <Box>
          <Typography variant="body2">每頁顯示</Typography>
          <Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(e.target.value);
              setPage(1);
            }}
            size="small"
            sx={{ ml: 1, minWidth: 80 }}
          >
            {[10, 20, 50, 100].map(n => (
              <MenuItem key={n} value={n}>{n} 則</MenuItem>
            ))}
          </Select>
        </Box>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, newPage) => setPage(newPage)}
          color="primary"
        />
      </Stack>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedProgram?.title}</DialogTitle>
        <DialogContent>
          {selectedProgram?.img && (
            <img src={selectedProgram.img} alt="Program" style={{ maxWidth: '100%', marginBottom: '10px' }} />
          )}
          <Typography variant="body1" component="div" dangerouslySetInnerHTML={sanitizedHtml(selectedProgram?.description || '')} />
          <Typography variant="body2" color="textSecondary">
            時間：{formatProgramTime(selectedProgram?.start_time)} - {formatProgramTime(selectedProgram?.end_time)}
          </Typography>
          {Array.isArray(selectedProgram?.person) && selectedProgram.person.length > 0 && (
            <Typography variant="body2" color="textSecondary">
              演出者：{selectedProgram.person.map(p => p.name).join(', ')}
            </Typography>
          )}
          {selectedProgram?.season_name && (
            <Typography variant="body2" color="textSecondary">
              節目：{selectedProgram.season_name}
            </Typography>
          )}
          {selectedProgram?.summary && (
            <Typography variant="body2" color="textSecondary">
              摘要：<span dangerouslySetInnerHTML={sanitizedHtml(selectedProgram.summary)} />
            </Typography>
          )}
          {selectedProgram?.url && (
            <Link href={selectedProgram.url} target="_blank" rel="noopener">
              更多訊息
            </Link>
          )}
          {Array.isArray(selectedProgram?.tag) && selectedProgram.tag.map((tag, i) => (
            <Chip key={typeof tag === 'string' ? tag : i} label={typeof tag === 'string' ? tag : tag.name || JSON.stringify(tag)} style={{ margin: '5px' }} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>關閉</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete} onClose={handleCancelDelete}>
        <DialogTitle>確認刪除</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除節目「{programToDelete?.title}」嗎？
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            此操作會將節目標記為已刪除。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>取消</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            確認刪除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PendingRecordingsPage;
